import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import * as Sentry from '@sentry/tanstackstart-react'
import { toast } from 'sonner'
import { HttpError } from '@/lib/http'

function makeQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        const message =
          error instanceof Error ? error.message : 'An error occurred'
        toast.error(message)
        if (!(error instanceof HttpError && error.status === 401)) {
          Sentry.captureException(error, {
            tags: { component: 'react-query', type: 'query' },
          })
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        console.log('Mutation error:', error)
        const message =
          error instanceof Error ? error.message : 'An error occurred'
        toast.error(message)
        if (!(error instanceof HttpError && error.status === 401)) {
          Sentry.captureException(error, {
            tags: { component: 'react-query', type: 'mutation' },
          })
        }
      },
    }),
  })
}

let browserQueryClient: QueryClient | undefined = undefined

export function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always create a new query client per request
    return makeQueryClient()
  }
  // Browser: reuse singleton (each user has their own browser)
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient()
  }
  return browserQueryClient
}

export function getContext() {
  return {
    queryClient: getQueryClient(),
  }
}

export function Provider({
  children,
  queryClient: client,
}: {
  children: React.ReactNode
  queryClient: QueryClient
}) {
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>
}
